/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useCallback, useEffect, useMemo } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';
import { buildPlayerFormCompleteness } from 'shared/analytics/player-form-completeness';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { AppDispatch } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { upsertTournamentUser } from 'modules/players/store/actions';
import PlayerForm, { SubmitPlayerForm } from '../components/player-form';

function PlayerCreatePage() {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { info } = useOrganizer();
  const { setHeaderContent } = useMainLayout();

  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: t`Игроки`, to: Pages.PLAYERS.INDEX },
          { label: t`Добавление игрока`, to: Pages.PLAYERS.CREATE },
        ]}
      />
    ),
    [t],
  );

  const onSubmit = useCallback<SubmitPlayerForm>(
    async ({ values }) => {
      if (!info) {
        return;
      }

      const photo = values.photo && values.photo[0] instanceof File ? (values.photo as File[]) : undefined;
      const height = values.height && !isNaN(Number(values.height)) ? Number(values.height) : undefined;
      const weight = values.weight && !isNaN(Number(values.weight)) ? Number(values.weight) : undefined;

      await dispatch(
        upsertTournamentUser({
          tournamentId: info.id,
          name: values.name,
          surname: values.surname,
          middleName: values.middleName,
          birthDate: values.birthDate,
          photo,
          height,
          weight,
        }),
      )
        .unwrap()
        .then((data) => {
          StepperMainTool.setByPath('stepper:main:createPlayer', true);
          trackEvent('Player Created', {
            source: 'players-page',
            ...buildPlayerFormCompleteness({
              middleName: values.middleName,
              birthDate: values.birthDate,
              photo: photo?.[0],
              height,
              weight,
            }),
          });
          navigate(Pages.PLAYERS.builders.view(data.id));
          toast.success(t`Игрок успешно создан`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось создать игрока`);
        });
    },
    [info, dispatch, navigate, t, toastApiError],
  );

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'w-full px-6 pt-6'}>
        <PlayerForm onSubmit={onSubmit} />
      </div>
    </>
  );
}

export default PlayerCreatePage;
